/**
 * App-side wiring for the `@heidi/plugin-core` door.
 *
 * Separate from `runtime-wiring.ts` on purpose: that module is the injection
 * point for the `@heidi/runtime` doors and reads a dozen `NEXT_PUBLIC_*` values
 * to do it. This one injects nothing from the environment — it hands the bridge
 * package the app's logger and the app's Tauri predicate, and that is the whole
 * of it — so keeping it here
 * makes the seam's single member visible in one short file rather than as one
 * more call in a long bootstrap.
 *
 * WHY THE SEAM EXISTS: `@heidi/plugin-core` is `type:bridge`, and
 * `packages/eslint-plugin-rules/module-boundaries.js` lets `type:bridge` depend
 * only on `type:bridge`, `type:util` and `type:schema`. `@heidi/runtime` is
 * `type:runtime`, so the five modules in that package that log — `windows/window`,
 * `events/managed-listen`, `events/recording-lifecycle` — cannot import
 * `@heidi/runtime/logger` at all, and `events/recording-lifecycle`'s guard cannot
 * import `@heidi/runtime/platform` either. Injecting both is the house pattern
 * (`capture`, `dictation`), and it keeps `@heidi/runtime/platform` the app's ONE
 * detection authority instead of growing a second one inside the bridge.
 *
 * The package defaults to a no-op sink, so forgetting this call costs desktop
 * diagnostics and nothing else — IPC keeps working. That is the right failure
 * mode and also an invisible one, which is why `plugin-core-wiring.test.ts`
 * asserts the wiring rather than trusting the absence of a crash.
 *
 * Imported at module scope from `_app.tsx`, beside `runtime-wiring`, so it
 * evaluates before any component mounts and therefore before any IPC call.
 */
import { configurePluginCore } from '@heidi/plugin-core';
import { logger } from '@heidi/runtime/logger';
import { isTauri } from '@heidi/runtime/platform';

configurePluginCore({ logger, isTauri });
